import React from 'react';
import { Link } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import { anchorRef, bindTrigger, usePopupState, bindPopover } from 'material-ui-popup-state/hooks';

import { Page } from '@occc-app/design-system';
import {
  AddIcon,
  TableContainer,
  Button,
  TableHeader,
  SearchForm,
  FilterButton,
  isFiltersSelected,
} from '@occc/ui-design-system';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { routeCreator } from '@occc-app/routes/Audience/utils';
import { useChainId } from '@occc-app/Chain/hooks';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { Stack } from '@mui/material';
import { AudiencesTable } from './components/AudiencesTable';
import { getAudienceFilter } from './utils';
import { useAudiencesFilterStatus } from './useAudiencesFilterStatus';

import { AudienceFilterFormValues } from '../types';
import { AudienceFilterForm } from '../components/AudienceFilterForm';
import { AUDIENCE_STATUS_ALL } from '../constants';
import { useGetAudiences } from '../../../dataAccessLayer/audience/useGetAudiences';

const PAGE_TITLE = 'Audiences';

export const STATUS_PARAM = 'status';

export const AudienceListPage: React.FC = () => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });

  const chainId = useChainId();

  const [status, setStatus] = useAudiencesFilterStatus(STATUS_PARAM);

  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const isUserPermittedToCreateAudience = useIsUserPermittedTo(Feature.AUDIENCE_CREATE);
  const isUserPermittedToEditAudience = useIsUserPermittedTo(Feature.AUDIENCE_EDIT);

  const audienceFilter = React.useMemo(() => {
    return getAudienceFilter(chainId, search, status);
  }, [chainId, search, status]);

  const { loading, error, data, paginationProps } = useGetAudiences({
    filter: audienceFilter,
    paginationInfo,
    onPageChange,
  });

  const actions = (
    <Button
      color='secondary'
      variant='contained'
      component={Link}
      startIcon={<AddIcon />}
      to={routeCreator.create()}
      role='button'
    >
      Create New Audience
    </Button>
  );

  const popoverProps = React.useMemo(() => {
    return {
      ...bindPopover({
        ...popupState,
      }),
    };
  }, [popupState]);

  const renderActions = () => {
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm
          onSubmit={(values) => {
            setSearch(values.search);
          }}
          initialValues={{ search }}
        />
        <FilterButton
          {...bindTrigger(popupState)}
          ref={anchorRef(popupState)}
          showBadge={isFiltersSelected([status], [AUDIENCE_STATUS_ALL])}
        />
      </Stack>
    );
  };

  const onSubmit = React.useCallback(
    (values: AudienceFilterFormValues) => {
      setStatus(values.status);
      popoverProps.onClose();
    },
    [popoverProps, setStatus],
  );

  const onReset = React.useCallback(() => {
    setStatus(undefined);
    popoverProps.onClose();
  }, [popoverProps, setStatus]);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      actions={isUserPermittedToCreateAudience && actions}
      pageHeaderProps={{
        title: PAGE_TITLE,
      }}
    >
      <TableContainer>
        <TableHeader title={PAGE_TITLE} actions={renderActions()} />
        <AudiencesTable
          audiences={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          isUserPermittedToEditAudience={isUserPermittedToEditAudience}
        />
        <Popover
          {...popoverProps}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          keepMounted
          sx={{
            '.MuiPopover-paper': {
              maxWidth: 460,
              minWidth: 300,
            },
          }}
        >
          <AudienceFilterForm
            onSubmit={onSubmit}
            onReset={onReset}
            initialValues={{ status: status || AUDIENCE_STATUS_ALL }}
          />
        </Popover>
      </TableContainer>
    </Page>
  );
};
